import HomeMenuComponent from '@/components/home-menu/home-menu.component';
import { INivel } from '@/shared/insaculacion/entity-commons/entity.factory';
import { EstadoRevision } from '@/shared/model/enumerations/estado-revision.model';
import { Dictamen } from '@/shared/model/respuesta.model';
import { IRevision, Revision } from '@/shared/model/revision.model';
import { Solicitud } from '@/shared/model/solicitud.model';
import { mixins } from 'vue-class-component';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { maxLength, minLength, required } from 'vuelidate/lib/validators';
import { mapGetters, mapMutations } from 'vuex';
import Dictamen2026Mixin from '../mixins/dictamen-2026-mixin';

const validations: any = {
  dictamenDiaspora: {
    comentarios: {
      required,
      min: minLength(500),
      max: maxLength(4000),
    },
  },
};

@Component({
  validations,
  computed: {
    ...mapGetters(['dictamen', 'evaluacion', 'revisorSeleccionado']),
  },
  methods: {
    ...mapMutations(['setDictamen', 'setEvaluacion']),
  },
})
export default class DictamenDiaspora2026Component extends mixins(HomeMenuComponent, Dictamen2026Mixin) {
  @Prop({ required: true })
  public solicitud: Solicitud;

  @Prop({ required: true })
  public revision: Revision;

  /**
   * Indica si se requiere una evaluación individual.
   */
  @Prop({ required: false, default: true })
  public evaluacionIndividual: boolean;

  public dictamenDiaspora: Dictamen = {
    comentarios: '',
  };

  public aceptada = EstadoRevision.ACEPTADA;
  public revisada = EstadoRevision.REVISADA;

  /**
   * 1 = Candidato
   * 2 = Nivel I
   * 3 = Nivel II
   * 4 = Nivel III
   */
  public dictamenPositivo: string[] = ['1', '2', '3', '4'];

  public get esAprobado(): boolean {
    return this.dictamenPositivo.includes(this.idEvaluacionParaDictamen);
  }

  public get esNoAprobado(): boolean {
    return this.idEvaluacionParaDictamen === '5';
  }

  public caracteresMinimos = 500;
  public caracteresMaximos = 4000;

  public revisiones: Array<IRevision> = [];

  setDictamen!: (dictamen: Dictamen) => void;

  setEvaluacion!: (evaluacion: INivel) => void;

  public readonly dictamen: Dictamen;

  public readonly evaluacion: INivel;

  public readonly revisorSeleccionado: number;

  mounted(): void {
    this.get();
  }

  /**
   * Detecta cuando cambia la revisión.
   */
  @Watch('revision')
  onRevisionChanged(): void {
    this.get();
  }

  /**
   * Detecta cuando cambia la solicitud.
   */
  @Watch('solicitud')
  onSolicitudChanged(): void {
    this.get();
  }

  /**
   * Obtiene el dictamen guardado.
   */
  public get(): void {
    // Limpiar comentarios anteriores
    this.dictamenDiaspora = {
      comentarios: '',
    };

    // Reiniciar validaciones
    this.$v?.dictamenDiaspora?.$reset();

    if (!this.solicitud || !this.revision) {
      return;
    }

    const respuesta: any = this.revision?.respuesta;
    const properties: any = respuesta?.properties || respuesta || {};

    if (properties?.dictamen) {
      this.dictamenDiaspora = {
        comentarios: properties.dictamen.comentarios || '',
      };
    }
  }

  /**
   * Obtiene la evaluación actual.
   */
  public get evaluacionDictamen(): INivel {
    const respuesta: any = this.revision?.respuesta;
    const properties: any = respuesta?.properties || respuesta || {};

    return properties?.evaluacion || this.evaluacion || {};
  }

  /**
   * Obtiene el resultado cualitativo.
   */
  public get descripcionComprensibleDictamen(): string {
    const respuesta: any = this.revision?.respuesta;
    const properties: any = respuesta?.properties || respuesta || {};

    return properties?.resultadoGralCualitativoFinal || '_____';
  }

  /**
   * Obtiene las valoraciones cualitativas.
   */
  public get valoracionesCualitativasDictamen(): any {
    const respuesta: any = this.revision?.respuesta;
    const properties: any = respuesta?.properties || respuesta || {};

    return properties?.valoracionesCualitativas || {};
  }

  public get hayValoracionesCualitativasDictamen(): boolean {
    return Object.keys(this.valoracionesCualitativasDictamen || {}).length > 0;
  }

  /**
   * Limita comentarios a 4000 caracteres.
   */
  public limitarComentariosDictamenNegativo(): void {
    const comentario = this.dictamenDiaspora?.comentarios || '';

    if (comentario.length > this.caracteresMaximos) {
      this.dictamenDiaspora.comentarios = comentario.substring(0, this.caracteresMaximos);
    }
  }

  /**
   * Marca el textarea como tocado para mostrar validaciones.
   */
  public tocarComentariosDictamenNegativo(): void {
    this.$v?.dictamenDiaspora?.comentarios?.$touch();
  }

  /**
   * Guarda el dictamen de Diáspora.
   */
  public guardarDictamen(): void {
    this.$v?.$touch();

    /**
     * Para No Aprobado los comentarios son obligatorios
     * y deben cumplir mínimo 500 / máximo 4000.
     */
    if (this.esNoAprobado && this.$v?.dictamenDiaspora?.$invalid) {
      return;
    }

    const respuesta: any = this.revision?.respuesta || {};
    const propertiesActuales: any = respuesta?.properties || respuesta || {};

    const dictamenEnviar: Dictamen = {
      comentarios: this.dictamenDiaspora?.comentarios || '',
    };

    const evaluacionEnviar = this.evaluacionDictamen;

    this.setEvaluacion(evaluacionEnviar);
    this.setDictamen(dictamenEnviar);

    const dictamenDiaspora2026 = {
      ...propertiesActuales,
      evaluacion: evaluacionEnviar,
      dictamen: dictamenEnviar,
      valido: true,
    };

    this.$emit('guardarDictamenDiaspora', dictamenDiaspora2026);
  }

  /**
   * Validación: comentario requerido.
   */
  get comentarioDictamenNegativoRequerido(): boolean {
    const campo = this.$v?.dictamenDiaspora?.comentarios;

    return campo?.$dirty && !campo?.required;
  }

  /**
   * Validación: mínimo 500 caracteres.
   */
  get comentarioDictamenNegativoMinimo(): boolean {
    const campo = this.$v?.dictamenDiaspora?.comentarios;

    return campo?.$dirty && !campo?.min;
  }

  /**
   * Validación: máximo 4000 caracteres.
   */
  get comentarioDictamenNegativoMaximo(): boolean {
    const campo = this.$v?.dictamenDiaspora?.comentarios;

    return campo?.$dirty && !campo?.max;
  }

  /**
   * Número de caracteres escritos.
   */
  get caracteresComentarioDictamenNegativo(): number {
    return Number(this.dictamenDiaspora?.comentarios?.length || 0);
  }

  /**
   * Caracteres faltantes para llegar a 500.
   */
  get caracteresFaltantesDictamenNegativo(): number {
    const faltantes = this.caracteresMinimos - this.caracteresComentarioDictamenNegativo;

    return faltantes > 0 ? faltantes : 0;
  }

  /**
   * Habilita guardar:
   * - Positivo: siempre.
   * - No aprobado: mínimo 500 caracteres.
   */
  get habilitarSave(): boolean {
    return this.esAprobado || (this.esNoAprobado && (this.dictamenDiaspora?.comentarios?.length ?? 0) >= this.caracteresMinimos);
  }

  /**
   * Obtiene el nivel de evaluación con redacción según género.
   */
  get nivelEvaluacionDictamen(): string {
    const nivelId = this.idEvaluacionParaDictamen;
    const esFemenino = this.esGeneroFemeninoDictamen;

    const nivelesPorGenero: Record<string, string> = {
      '1': esFemenino ? 'Candidata' : 'Candidato',
      '2': esFemenino ? 'Investigadora Nacional Nivel I' : 'Investigador Nacional Nivel I',
      '3': esFemenino ? 'Investigadora Nacional Nivel II' : 'Investigador Nacional Nivel II',
      '4': esFemenino ? 'Investigadora Nacional Nivel III' : 'Investigador Nacional Nivel III',
      '5': 'No Aprobado',
    };

    return nivelesPorGenero[nivelId] || '_____';
  }

  get idEvaluacionParaDictamen(): string {
    const respuesta: any = this.revision?.respuesta || {};

    return String(respuesta?.evaluacion?.id || '');
  }

  /**
   * Indica si la persona solicitante es mujer.
   */
  get esGeneroFemeninoDictamen(): boolean {
    return this.generoSolicitanteDictamen === 'FEMENINO' || this.generoSolicitanteDictamen === 'F';
  }

  /**
   * Indica si la persona solicitante es hombre.
   */
  get esGeneroMasculinoDictamen(): boolean {
    return this.generoSolicitanteDictamen === 'MASCULINO' || this.generoSolicitanteDictamen === 'M';
  }

  /**
   * Obtiene el género de la persona solicitante.
   *
   * Puede venir desde:
   * - solicitud.solicitante.genero
   * - SOLICITUD_RIZOMA.solicitante.sexo.nombre
   */
  get generoSolicitanteDictamen(): string {
    const solicitudAny: any = this.solicitud || {};

    return String(
      solicitudAny?.solicitante?.genero ||
        this.solicitudRizomaDictamen?.solicitante?.sexo?.nombre ||
        solicitudAny?.properties?.SOLICITUD_RIZOMA?.solicitante?.sexo?.nombre ||
        ''
    ).toUpperCase();
  }

  /**
   * Obtiene la información principal de la solicitud Rizoma.
   */
  get solicitudRizomaDictamen(): any {
    const solicitudAny: any = this.solicitud;

    return solicitudAny?.SOLICITUD_RIZOMA || solicitudAny?.properties?.SOLICITUD_RIZOMA || {};
  }

  get fraccionArticulo19Dictamen(): string {
    const nivelId = this.idEvaluacionParaDictamen;

    const fraccionesPorNivel: any = {
      '1': 'I', // Candidato
      '2': 'II', // Investigador Nacional Nivel I
      '3': 'III', // Investigador Nacional Nivel II
      '4': 'IV', // Investigador Nacional Nivel III
    };

    return fraccionesPorNivel[nivelId] || '_____';
  }

  get descripcionArticulo19Dictamen(): string {
    const nivelId = this.idEvaluacionParaDictamen;
    const esFemenino = this.esGeneroFemeninoDictamen;

    const descripcionesPorNivel: Record<string, string> = {
      '1': esFemenino ? 'Candidata' : 'Candidato',
      '2': esFemenino ? 'Investigadora Nacional Nivel I' : 'Investigador Nacional Nivel I',
      '3': esFemenino ? 'Investigadora Nacional Nivel II' : 'Investigador Nacional Nivel II',
      '4': esFemenino ? 'Investigadora Nacional Nivel III' : 'Investigador Nacional Nivel III',
    };

    return descripcionesPorNivel[nivelId] || '_____';
  }
}
